import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const Vision = () => {
  const { t } = useLanguage()

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-vision.jpg"
            placeholder="/images/placeholders/hero-vision-placeholder.jpg"
            alt="Future City"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">OUR VISION</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('vision.hero.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="text-2xl md:text-3xl font-mono text-gray-300 mb-6 tracking-wide">
              "{t('vision.hero.subtitle')}"
            </motion.p>
            <motion.p variants={textReveal} className="section-description">
              {t('vision.content')}
            </motion.p>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* Vision Goals - 网格纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">STRATEGIC GOALS</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('vision.goals.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('vision.goals.subtitle')}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              { icon: 'public', title: t('vision.goals.ecosystem.title'), desc: t('vision.goals.ecosystem.desc') },
              { icon: 'group', title: t('vision.goals.collaboration.title'), desc: t('vision.goals.collaboration.desc') },
              { icon: 'code', title: t('vision.goals.transformation.title'), desc: t('vision.goals.transformation.desc') }
            ].map((item, index) => (
              <GlareCard key={index}>
                <span className="material-symbols-outlined text-3xl text-white/30 mb-4">{item.icon}</span>
                <h3 className="section-title mb-4">{item.title}</h3>
                <p className="section-desc">{item.desc}</p>
              </GlareCard>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Vision - 渐变纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">IMPACT</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('vision.impact.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm">
              {t('vision.impact.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <motion.div variants={staggerItem} className="metal-surface p-8">
              <h3 className="text-lg font-bold text-white mb-6 uppercase tracking-widest">{t('vision.impact.developers.title')}</h3>
              <ul className="space-y-4">
                {[
                  t('vision.impact.developers.1'),
                  t('vision.impact.developers.2'),
                  t('vision.impact.developers.3')
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-1 h-1 bg-white/50 rounded-full mt-2"></span>
                    <span className="text-gray-400 text-sm font-mono">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-8">
              <h3 className="text-lg font-bold text-white mb-6 uppercase tracking-widest">{t('vision.impact.industry.title')}</h3>
              <ul className="space-y-4">
                {[
                  t('vision.impact.industry.1'),
                  t('vision.impact.industry.2'),
                  t('vision.impact.industry.3')
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-1 h-1 bg-white/50 rounded-full mt-2"></span>
                    <span className="text-gray-400 text-sm font-mono">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - 深灰背景 */}
      <section className="relative z-10 py-32 px-6 md:px-20 bg-dark-gray">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={staggerContainer}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={textReveal} className="section-label justify-center flex mb-6">JOIN US</motion.div>
          <motion.h2 variants={textReveal} className="text-4xl md:text-5xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
            {t('vision.cta.title')}
          </motion.h2>
          <motion.p variants={textReveal} className="text-gray-500 text-lg font-mono mb-12">
            {t('vision.cta.subtitle')}
          </motion.p>
          <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/careers" className="section-cta group">
              <span>{t('vision.cta.join')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
              <span>{t('vision.cta.learn')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}

export default Vision
